import type { QuickPreviewTabName } from '@mimir/common/types/UIOnlySections';
import type { LoadInQuickPreviewPayload, LoadRelatedItemInQuickPreviewPayload, QuickPreview, SetCollapsedPayload, SetIsItemMetadataLoadingPayload, UpdateCurrentPreviewItemPayload } from '../model/quickPreviewOps';
import type { SearchState } from '../model/searchState';
import type { UserInterfaceSettings } from '../util/localStorage';
import { createItemMediaPlayerOpsStub, type ItemMediaPlayerOps, type PostLoadSeek } from '../model/itemMediaPlayerOps';
import Vue, { computed, reactive, ref, shallowRef, type Ref, markRaw } from 'vue';
import type { ClientItem, LoadedItem } from '@mimir/common/dto/searchResultHit';
import type { RelatedItem } from '@mimir/common/dto/relationModels';
import cloneDeep from 'lodash/cloneDeep';
import type { VueDeleteValue, VueSetValue } from '@mimir/common/util/general';
import { handleMdfUpdatesToItem } from '@mimir/common/mdf/update';
import type { MetadataSnapshot } from '@mimir/common/dto/mdfModels';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { ItemState } from '@mimir/common/dto/itemState';
import { isDeleting } from '@mimir/common/dto/itemModels';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import isNumber from 'lodash/isNumber';
import { toRelatedItem } from '../model/toRelatedItem';
import { getMediaDuration } from '@mimir/common/util/item';
import type { ThumbnailClick, ThumbnailStripClick } from '../pages/search/components/search-page-interfaces';
import { constructFolderMetadataItem, isFolderMetadataItem, type FolderMetadataItem } from '@mimir/web/pages/item/components/metadataUtils';
import type { FolderDto, FolderViewModel } from '@mimir/common/dto/folderModels';
import { isFolderViewModel } from '../pages/folders/util';
import noop from 'lodash/noop';

export interface QuickPreviewServices {
  useNewBufferingLogic(): boolean,
  logErrorAndShowToast(message: string, err: unknown, devMessage: string, requireExplicitClose: boolean): void,
  getFolder(folderId: string): Promise<FolderDto>
}

interface MutableQuickPreviewState {
  previewedObject: Ref<{ type: 'item', item: ClientItem } | { type: 'folder', folder: FolderMetadataItem } | null>,
  selectedRelatedItem: Ref<RelatedItem | null>,
  selectedTab: Ref<QuickPreviewTabName>,
  previewCollapsed: Ref<boolean>,
  recentlyPreviewed: Ref<(ClientItem | FolderMetadataItem)[]>,
  isItemMetadataLoadingMap: Ref<Record<string, boolean>>,
  isFolderMetadataLoading: Ref<boolean>,
  onUnsavedChangesHandledCallback: Ref<(() => void) | null>,
  isFullscreen: Ref<boolean>,
  pinnedSelection: Ref<ClientItem | FolderMetadataItem | null>,
  pinningEnabled: Ref<boolean>
  // The last selected preview object. It represents both currentFolder in folder tree and item/folder in search result.
  // This is used to set preview to current selected item/folder when user unpins the pinned item/folder.
  unpinnedPreviewItem: Ref<ClientItem | FolderMetadataItem | FolderViewModel | null>,
}

export function createQuickPreview(services: QuickPreviewServices, uiSettings: UserInterfaceSettings): QuickPreview {
  const initialSelectedTab: QuickPreviewTabName = uiSettings.thirdColumn !== 'metadata' ? 'metadata' : 'logging';
  // Shallow: the player publishes its own observable state, and a deep ref would make `this` inside it a reactive proxy.
  const quickPreviewMediaPlayerOps = shallowRef<ItemMediaPlayerOps>(createItemMediaPlayerOpsStub());


  const mutableState : MutableQuickPreviewState = {
    previewedObject: ref<{ type: 'item', item: ClientItem } | null>(null),
    selectedRelatedItem: ref<RelatedItem | null>(null),
    selectedTab: ref<QuickPreviewTabName>(initialSelectedTab),
    previewCollapsed: ref<boolean>(false),
    recentlyPreviewed: ref<(ClientItem | FolderMetadataItem)[]>([]),
    // Wrap this reactive object in a Ref for consistency sake. Not
    // technically necessary.
    isItemMetadataLoadingMap: ref<Record<string, boolean>>(reactive({})),
    isFolderMetadataLoading: ref<boolean>(false),
    onUnsavedChangesHandledCallback: ref(null),
    isFullscreen: ref(false),
    pinnedSelection: ref(null),
    pinningEnabled: ref(false),
    unpinnedPreviewItem: ref(null),
  };

  const setPreviewObject = (payload: ClientItem | FolderMetadataItem | null) => {
    if (payload === null) {
      mutableState.previewedObject.value = null;
    } else if (isFolderMetadataItem(payload)) {
      mutableState.previewedObject.value = { type: 'folder', folder: payload };
    } else {
      mutableState.previewedObject.value = { type: 'item', item: payload };
    }
  };

  const currentItem = computed<ClientItem | null>(() => {
    if (mutableState.previewedObject.value?.type === 'item') {
      return mutableState.previewedObject.value.item;
    }
    return null;
  });

  const currentFolder = computed<FolderMetadataItem | null>(() => {
    if (mutableState.previewedObject.value?.type === 'folder') {
      return mutableState.previewedObject.value.folder;
    }
    return null;
  });
  let pendingFolderRequestId : string | null = null;

  function loadFolderPreview(item: ClientItem | FolderViewModel | FolderMetadataItem): void {
    // When folder is selected from folder tree, loadAndSetFolder already fetched
    // full folder with metadata, so we can use it directly without another API call
    if (item.metadata && !isFolderMetadataItem(item) && isFolderViewModel(item)) {
      const folderMetadataItem = constructFolderMetadataItem(item);
      setPreviewObject(cloneDeep(folderMetadataItem));
      addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, folderMetadataItem);
      return;
    }
    const folderId = item.id;
    pendingFolderRequestId = folderId;
    mutableState.isFolderMetadataLoading.value = true;

    services.getFolder(folderId).then((folderDto) => {
      if (folderId !== pendingFolderRequestId) {
        return;
      }
      const folderMetadataItem = constructFolderMetadataItem(folderDto);
      setPreviewObject(cloneDeep(folderMetadataItem));
      addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, folderMetadataItem);
    }).catch((error) => {
      if (folderId === pendingFolderRequestId) {
        services.logErrorAndShowToast('Failed to load folder metadata.', error, 'ex in setPreviewSelection folder fetch', false);
      }
    }).finally(() => {
      if (folderId === pendingFolderRequestId) {
        mutableState.isFolderMetadataLoading.value = false;
      }
    });
  }

  const setCurrentItem = (item: ClientItem | FolderViewModel | FolderMetadataItem | null) => {
    const previousItemId = currentItem.value?.id ?? currentFolder.value?.id;
    if (item && item.id === previousItemId) {
      /**
           * TODO: If we already have this item as the currentItem, we should
           * probably updating the existing item instead of overwriting it, since
           * we mutate the currentItem with client state from various places.
           */
      if (item.itemType === ItemType.FOLDER) {
        console.warn('setPreviewItem: folder is already the current folder');
      } else {
        console.warn('setPreviewItem: item is already the current item');
      }
    }

    if (item === undefined) {
      throw new Error('Item is undefined (not null). This the calling code is somehow violating the type safety');
    }

    mutableState.selectedRelatedItem.value = null;
    pendingFolderRequestId = null;

    if (item === null) {
      setPreviewObject(null);
      return;
    }

    if (item.itemType === ItemType.FOLDER) {
      loadFolderPreview(item);
      return;
    }

    setPreviewObject(cloneDeep(item));
    if (currentItem.value !== null) {
      addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, currentItem.value);
    } else {
      quickPreviewMediaPlayerOps.value.clear();
    }
  };

  function getMutableItem(): ClientItem | null {
    if (mutableState.previewedObject.value?.type === 'item') {
      return mutableState.previewedObject.value.item;
    }
    return null;
  }
  const publicState = { ...mutableState, currentItem, currentFolder };

  const quickPreview = {
    get state() { return publicState; },
    player: computed(() => quickPreviewMediaPlayerOps.value),
    ops: {
      setIsItemMetadataLoading(payload: SetIsItemMetadataLoadingPayload): void {
        Vue.set(mutableState.isItemMetadataLoadingMap.value, payload.id, payload.loading);
      },
      setSelectedQuickPreviewTab(payload: QuickPreviewTabName): void {
        mutableState.selectedTab.value = payload;
      },
      unload(): void {
        setCurrentItem(null);
      },
      updateCurrentPreviewItem(payload: UpdateCurrentPreviewItemPayload): void {
        const mutableItem = getMutableItem();
        if (!mutableItem || payload.id !== mutableItem.id) {
          return;
        }
        const keysToIterateOver = Object.keys(payload.update) as (keyof LoadedItem)[];
        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            applyMetadataUpdate(mutableItem, payload.update);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(mutableItem, prop);
          } else {
            Vue.set(mutableItem, prop, payload.update[prop]);
          }
        }
      },
      setSearchPlayerItemMetadataSnapshot(data: MetadataSnapshot | undefined): void {
        const mutableItem = getMutableItem();
        if (mutableItem) {
          Vue.set(mutableItem, 'metadataSnapshot', data);
        }
      },

      // Actions
      emitPlayerError(ev: ErrorEvent): void {
        // Suppress error for now if its a placeholder. This should be removed once we have a quick preview
        // component for place holders (likely a drag and drop component. Issue #426)
        const item = currentItem.value;
        if (item !== null &&
            item.itemType === ItemType.VIDEO &&
            item.itemState !== ItemState.NEW &&
            item.itemState !== ItemState.HIGHRES_AVAILABLE &&
            !isDeleting(item) &&
            item.transcodingState &&
            item.transcodingState !== AsyncOperationState.PROGRESSING &&
            item.transcodingState !== AsyncOperationState.INITIATED) {
          services.logErrorAndShowToast('Failed to load video', ev, '', false);
        }
      },
      setCollapsed(payload: SetCollapsedPayload): void {
        const { shouldBeCollapsed } = payload;
        if (shouldBeCollapsed) {
          quickPreviewMediaPlayerOps.value.pause();
        }
        mutableState.previewCollapsed.value = shouldBeCollapsed;
      },
      load(payload: LoadInQuickPreviewPayload): void {
        const { preview, fromHistory, autoPlayFrom, timeToSeekInMs, searchState, unpin, userMadeMetadataChanges, onLoaded } = payload;
        const isRefreshSearch = payload.isRefreshSearch ?? false;

        if (handlePinningOnLoad({ preview, unpin, userMadeMetadataChanges, onLoaded, state: mutableState, isRefreshSearch })) {
          return;
        }
        const hasCurrentPreview = currentItem.value !== null || currentFolder.value !== null;
        if (isRefreshSearch && hasCurrentPreview && preview?.itemType === ItemType.FOLDER) {
          // A search refresh in a currentFolder re-issues the folder preview on every cycle. Do not let it
          // take over the panel and deselect an item/folder the user is actively previewing.
          return;
        }

        if (preview?.id === currentItem.value?.id) {
          quickPreview.ops.deselectRelatedItem();
          seekSameItemIfRequested(timeToSeekInMs, quickPreviewMediaPlayerOps.value);
          return;
        }

        const performLoad = () => {
          setupMediaPlayerForPreview(
            preview,
            autoPlayFrom,
            timeToSeekInMs,
            quickPreviewMediaPlayerOps.value,
            mutableState.previewCollapsed.value,
            services.useNewBufferingLogic(),
          );
          mutableState.selectedRelatedItem.value = null;
          if (fromHistory) {
            // When navigating via history, to reduce user confusion we clear selection in search, because there's no guarantee that the item
            // that will be previewed will be visible in the current search result.
            searchState.ops.clearSelectedSearchItems();
          }
          setCurrentItem(preview);
          onLoaded();
        };

        const currentPreview = (currentItem.value ?? currentFolder.value)?.id;
        const changeCurrentPreview = preview?.id !== currentPreview;
        if (userMadeMetadataChanges && changeCurrentPreview) {
          quickPreview.ops.setOnUnsavedChangesHandled(performLoad);
          return;
        }

        performLoad();
      },
      loadRelatedItem(payload: LoadRelatedItemInQuickPreviewPayload): void {
        const { event } = payload;
        const wasPlaying = !quickPreviewMediaPlayerOps.value.getPausedObs().currentValue();
        const relatedItem = toRelatedItem(event.item);
        if (relatedItem === null) {
          throw new Error('Requested item to load is not a related item');
        }
        const isDifferentItem = relatedItem.id !== mutableState.selectedRelatedItem.value?.id;
        mutableState.selectedRelatedItem.value = relatedItem;
        addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, relatedItem);
        if (relatedItem.itemType === ItemType.VIDEO || relatedItem.itemType === ItemType.AUDIO) {
          if (isDifferentItem) {
            const useNewBufferingLogic = services.useNewBufferingLogic();
            const flags = {
              useNewBufferingLogic,
            };

            quickPreviewMediaPlayerOps.value.setItemId(relatedItem.id, relatedItem.itemType, flags);
          }
          mutableState.previewCollapsed.value = false;
        } else if (isDifferentItem) {
          quickPreviewMediaPlayerOps.value.clear();
        }

        schedulePlaybackAtClickedPosition(event, quickPreviewMediaPlayerOps.value, wasPlaying);
      },
      deselectRelatedItem(): void {
        if (mutableState.selectedRelatedItem.value === null) {
          return;
        }
        const root = currentItem.value;
        if (root !== null && (root.itemType === ItemType.VIDEO || root.itemType === ItemType.AUDIO)) {
          setupMediaPlayerForPreview(
            root,
            undefined,
            undefined,
            quickPreviewMediaPlayerOps.value,
            mutableState.previewCollapsed.value,
            services.useNewBufferingLogic(),
          );
        } else {
          quickPreviewMediaPlayerOps.value.clear();
        }
        mutableState.selectedRelatedItem.value = null;
        if (root !== null) {
          addToRecentlyPreviewed(mutableState.recentlyPreviewed.value, root);
        }
      },
      unloadSearchPlayer(): void {
        quickPreviewMediaPlayerOps.value.clear();
      },
      updateCurrentPreviewItemMutation(payload: UpdateCurrentPreviewItemPayload) {
        const mutableItem = getMutableItem();
        if (!mutableItem || payload.id !== mutableItem.id) {
          return;
        }

        /*
          `payload.retrievedFields` contains a list of keys fetched from the server.
          Since `undefined` values are not transmitted over the API, we combine the
          fetched keys with those available in the `payload.update` object.

          This ensures that the client state is updated with the most recent data
          while correctly handling stale state updates.
        */
        const uniqueKeys = new Set([...Object.keys(payload.update), ...(payload.retrievedFields ?? [])]);
        const keysToIterateOver = Array.from(uniqueKeys) as (keyof LoadedItem)[];

        for (const prop of keysToIterateOver) {
          if (prop === 'metadata' && payload.update.metadata !== undefined) {
            applyMetadataUpdate(mutableItem, payload.update);
          } else if (payload.update[prop] === undefined) {
            Vue.delete(mutableItem, prop);
          } else {
            Vue.set(mutableItem, prop, payload.update[prop]);
          }
        }
      },
      setPlayer(player: ItemMediaPlayerOps): void {
        quickPreviewMediaPlayerOps.value.clear();
        quickPreviewMediaPlayerOps.value = player;
      },
      setOnUnsavedChangesHandled(callback: () => void): void {
        mutableState.onUnsavedChangesHandledCallback.value = callback;
      },
      unsavedChangesHandled(cancelled: boolean): void {
        if (!cancelled && mutableState.onUnsavedChangesHandledCallback.value) {
          mutableState.onUnsavedChangesHandledCallback.value();
        }
        mutableState.onUnsavedChangesHandledCallback.value = null;
      },
      setFullscreen(fullscreen: boolean): void {
        mutableState.isFullscreen.value = fullscreen;
      },
      togglePin(searchState: SearchState, userMadeMetadataChanges: boolean): void {
        if (!mutableState.pinningEnabled.value) {
          return;
        }
        if (mutableState.pinnedSelection.value) {
          mutableState.pinnedSelection.value = null;
          // When unpinning, if there is a selected item in search view or in folder tree, user should see it in quick preview.
          if (mutableState.unpinnedPreviewItem.value) {
            quickPreview.ops.load({ preview: mutableState.unpinnedPreviewItem.value, searchState, unpin: false, userMadeMetadataChanges, onLoaded: noop });
          }
        } else if (currentItem.value || currentFolder.value) {
          mutableState.pinnedSelection.value = currentItem.value || currentFolder.value;
        }
      },
      enablePinning(searchState: SearchState): void {
        mutableState.pinningEnabled.value = true;
        // If there is already a pinned item when pinning gets enabled, we should load it in the quick preview.
        // This can happen when user navigates back to search page from scheduler page.
        const pinned = mutableState.pinnedSelection.value;
        if (pinned !== null) {
          quickPreview.ops.load({ preview: pinned, searchState, unpin: false, userMadeMetadataChanges: false, onLoaded: noop });
        }
      },
      disablePinning(): void {
        mutableState.pinningEnabled.value = false;
      },
    }
  };

  // markRaw: the quickPreview singleton is passed as a prop at many sites. Under
  // Vue 2.7, crossing that observation boundary deep-observes the container and
  // auto-unwraps `player` (a ComputedRef) and the `state` refs in place, so
  // `.value` becomes undefined globally and permanently (issue #20655). markRaw
  // stops the container from being observed; the inner refs keep their reactivity.
  return markRaw(quickPreview);
}

/**
 * If previewable, track recently previewed items. If an item is already in the list, to avoid duplicates, we remove the old copy
 * and add a new clone to the top of the list. Items may have changed in the meantime.
 *
 */
function addToRecentlyPreviewed(recentlyPreviewedList: (ClientItem | RelatedItem | FolderMetadataItem)[], itemToAdd: ClientItem | RelatedItem | FolderMetadataItem) {

  const indexOfExisting = recentlyPreviewedList.findIndex(item => item.id === itemToAdd.id);
  if (indexOfExisting >= 0) {
    recentlyPreviewedList.splice(indexOfExisting, 1);
  }
  recentlyPreviewedList.unshift(cloneDeep(itemToAdd));
}

function schedulePlaybackAtClickedPosition(
  event: ThumbnailStripClick | ThumbnailClick,
  playerOps: ItemMediaPlayerOps,
  wasPlaying: boolean,
): void {
  const mediaDuration = getMediaDuration(event.item);
  if (!mediaDuration) {
    return;
  }
  const time = isThumbnailStripClick(event) ? mediaDuration * event.percentageOfVideo : 0;
  setTimeout(() => {
    if (wasPlaying) {
      playerOps.play(time);
    } else {
      playerOps.seekToVirtualClipMediaPosition({ approximate: time / 1000.0 });
    }
  }, 100);
}

function isThumbnailStripClick(event: ThumbnailStripClick | ThumbnailClick): event is ThumbnailStripClick {
  return 'percentageOfVideo' in event;
}

const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);

function applyMetadataUpdate(mutableItem: ClientItem, update: Partial<LoadedItem>): void {
  if (update.metadata === undefined) {
    return;
  }
  let metadataVersion = update.metadataVersion;
  if (metadataVersion === undefined) {
    console.warn('applyMetadataUpdate: metadata update without metadataVersion');
    metadataVersion = 0;
  }
  handleMdfUpdatesToItem(mutableItem, update.metadata, metadataVersion, vueSet, vueDeleter);
}

function setupMediaPlayerForPreview(
  preview: ClientItem | FolderViewModel | FolderMetadataItem | null,
  autoPlayFrom: number | undefined,
  timeToSeekInMs: number | undefined,
  playerOps: ItemMediaPlayerOps,
  isCollapsed: boolean,
  useNewBufferingLogic: boolean,
): void {
  if (preview === null || !playerOps.getVisibleObs().currentValue()) {
    return;
  }
  if (preview.itemType !== ItemType.VIDEO && preview.itemType !== ItemType.AUDIO) {
    return;
  }

  let autoplay: boolean;
  if (isCollapsed) {
    autoplay = false;
  } else if (isNumber(autoPlayFrom)) {
    autoplay = true;
  } else {
    autoplay = !playerOps.getPausedObs().currentValue();
  }
  const flags = { useNewBufferingLogic };
  const seekPositionInMs = isNumber(autoPlayFrom) ? autoPlayFrom : (timeToSeekInMs ?? 0);
  const postLoadOperation: PostLoadSeek = {
    type: 'seek',
    positionInMs: seekPositionInMs,
    play: autoplay,
  };
  playerOps.setItemId(preview.id, preview.itemType, flags, { postLoadOperation });
}

function seekSameItemIfRequested(timeToSeekInMs: number | undefined, playerOps: ItemMediaPlayerOps): void {
  if (timeToSeekInMs !== undefined) {
    playerOps.seekToClampedSourceMediaPosition({ approximate: timeToSeekInMs / 1000.0 });
  }
}

interface HandlePinningOnLoadParams {
  preview: ClientItem | FolderViewModel | FolderMetadataItem | null;
  unpin: boolean;
  userMadeMetadataChanges: boolean;
  onLoaded: () => void;
  state: Pick<MutableQuickPreviewState, 'pinnedSelection' | 'unpinnedPreviewItem' | 'pinningEnabled'>;
  isRefreshSearch: boolean;
}

/** Returns true if the load should be aborted due to pinning constraints. */
function handlePinningOnLoad(params: HandlePinningOnLoadParams): boolean {
  const { preview, unpin, userMadeMetadataChanges, onLoaded, state, isRefreshSearch } = params;
  // A background search refresh is not a user selection, so it must not overwrite
  // unpinnedPreviewItem (the preview restored when the user unpins).
  if (!isRefreshSearch && preview && preview.id !== state.pinnedSelection.value?.id) {
    state.unpinnedPreviewItem.value = preview;
  }

  if (unpin === true && state.pinningEnabled.value) {
    // When pinningEnabled and unpin is true, it means that the user explicitly wants to unpin the current pinned item (if any) when loading the new item.
    state.pinnedSelection.value = null;
    return false;
  }

  if (state.pinningEnabled.value && state.pinnedSelection.value && preview?.id !== state.pinnedSelection.value.id) {
    // In search view when pinned item has unsaved changes and user selects a different item to preview
    // we do not change the preview but the recent selected item should be shown as selected in search list.
    // The item selection in search list is done by onLoaded callback which is provided by searchState -> toggleSearchItemSelection.
    // Since we are not changing the preview when there is pinned item and due to unsaved changes the toggleSearchItemSelection does not change search item selection,
    // so we need to call onLoaded callback here to ensure that the recent selected item in search list is selected (in search state) even when there are unsaved changes in pinned item.
    if (userMadeMetadataChanges && !isRefreshSearch) {
      onLoaded();
    }
    return true;
  }

  return false;
}
